// src/hooks/use-assign-writing-credit.ts
// VIB-789: Etter at et tilbud er konvertert til prosjekt, oppretter dette
// budget_line_assignments på Tilbudsskriving-linja for hver writer i
// offer.writers, med approved/completed_at og locker linja.
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { invalidateFor } from "@/lib/cache-registry";

interface Writer {
  user_id: string;
  share_pct: number;
}

interface AssignArgs {
  projectId: string;
  writers: Writer[];
  /** Total timer på Tilbudsskriving-linja (auto-generert fra tilbudet). */
  totalHours: number;
}

export function useAssignWritingCreditOnProject() {
  const qc = useQueryClient();
  return useMutation<void, Error, AssignArgs>({
    mutationFn: async ({ projectId, writers, totalHours }) => {
      if (totalHours <= 0 || writers.length === 0) return;

      // Finn Tilbudsskriving-linja på prosjektet. Vi matcher på
      // description-prefix "Tilbudsskriving" siden den er auto-generert.
      const { data: lines, error: linesErr } = await supabase
        .from("budget_lines")
        .select("id, description")
        .eq("project_id", projectId);
      if (linesErr) throw linesErr;
      const line = (lines ?? []).find((l) =>
        (l.description ?? "").toLowerCase().startsWith("tilbudsskriving"),
      );
      if (!line) return;

      // Beregn timer per writer. Rund til 1 desimal og korriger siste
      // writer slik at sum stemmer.
      const hoursList = writers.map((w) =>
        Math.round(((totalHours * w.share_pct) / 100) * 10) / 10,
      );
      const sumSoFar = hoursList.reduce((s, h) => s + h, 0);
      hoursList[hoursList.length - 1] += Math.round((totalHours - sumSoFar) * 10) / 10;

      const completedAt = new Date().toISOString();
      const rows = writers.map((w, i) => ({
        budget_line_id: line.id,
        user_id: w.user_id,
        hours: hoursList[i],
        task_status: "approved" as const,
        completed_at: completedAt,
      }));

      const { error: insertErr } = await supabase
        .from("budget_line_assignments")
        .insert(rows);
      if (insertErr) throw insertErr;

      const { error: lockErr } = await supabase
        .from("budget_lines")
        .update({ is_locked: true })
        .eq("id", line.id);
      if (lockErr) throw lockErr;
    },
    onSuccess: async (_data, { projectId }) => {
      await invalidateFor(qc, "budget_lines", { projectId });
      await invalidateFor(qc, "budget_line_assignments", { projectId });
    },
  });
}
